import moment from 'moment';
import { SubscriptionActions } from 'core/enums/subscription-actions';
import { VasCodesType } from 'core/enums/vas-code';
import { Quote } from 'core/models/account/amount-to-pay.dto';
import { ApiActionDTO } from 'core/models/general/api-action.dto';
import { HardwareDTO } from 'core/models/product/hardware.dto';
import { LinkPackageEligibleDTO, LinkPackageEligibleItem, QualifyingProductDTO, QuoteDetails, StreamQuantity, VASAddonDTO, VASAgreementDTO, VasProductModel } from 'core/models/product/link-package.dto';
import { PackagesDTO } from 'core/models/product/packages.dto';
import { Product } from 'core/models/product/product';
import { ShowmaxCodeLocal, ShowmaxCodePL } from 'core/models/product/showmax';
import { AvailableProductAction } from 'core/stores/action/enums/action-map';
import { Util } from './common.utils';

export class ProductUtils {
  static MapProductContentAndData(
    content?: PackagesDTO[] | HardwareDTO[],
    data?: Partial<Product>[],
    channels?: any,
    channelGenres?: any
  ): Product[] {
    return ((data?.length && [
      ...data.map((current) => {
        const matching = content?.find(({ SelfServiceProductCodes }) =>
          ProductUtils.MatchCode(SelfServiceProductCodes, current.serviceCode ?? current.model, true)
        );

        if (current.addons?.length) {
          const addons = current.addons.map((addon) => {
            const matchingAddon = content?.find(({ SelfServiceProductCodes }) =>
              ProductUtils.MatchCode(SelfServiceProductCodes, addon.serviceName)
            );
            if (matchingAddon) {
              return Product.FromUmbracoPackageDTO(matchingAddon, addon as Product, channels, channelGenres);
            }
            return addon;
          });
          current.addons = addons;
        }

        if (current.flexProducts?.length) {
          const flexProducts = current.flexProducts.map((flexProduct) => {
            const matchingFlexProduct = content?.find(({ SelfServiceProductCodes }) =>
              ProductUtils.MatchCode(SelfServiceProductCodes, flexProduct.serviceName)
            );
            if (matchingFlexProduct) {
              return Product.FromUmbracoPackageDTO(matchingFlexProduct, flexProduct as Product, channels, channelGenres);
            }
            return flexProduct;
          });
          current.flexProducts = flexProducts;
        }

        if (matching) {
          return Product.FromUmbracoPackageDTO(matching, current as Product, channels, channelGenres);
        }
        return current;
      }),
    ]) ||
      []) as Product[];
  }

  static MapBackgroundPatterns(content?: PackagesDTO[], data?: Partial<Product>[]): Product[] {
    return ((data?.length && [
      ...data.map((current) => {
        const matching = content?.find(({ SelfServiceProductCodes }) =>
          ProductUtils.MatchCode(SelfServiceProductCodes, current.serviceCode, true)
        );

        current = ProductUtils.AugmentProductBackgroundPatterns(matching, current);
        return current;
      }),
    ]) ||
      []) as Product[];
  }

  static MapHardwareContentAndData(content?: HardwareDTO[], data?: Partial<Product>[]): Product[] {
    return ((data?.length && [
      ...data.map((current) => {
        const matching = content?.find(({ SelfServiceProductCodes }) => ProductUtils.MatchCode(SelfServiceProductCodes, current.model));
        return ProductUtils.AugmentProductBase(matching, current);
      }),
    ]) ||
      []) as Product[];
  }

  static MapEligiblePackagesAndContent(eligiblePackages: LinkPackageEligibleItem[], content?: PackagesDTO[]) {
    return ((eligiblePackages?.length && [
      ...eligiblePackages.map((current) => {
        const matching = content?.find(({ SelfServiceProductCodes }) =>
          ProductUtils.MatchCode(SelfServiceProductCodes, current.serviceName)
        );
        if (matching) {
          return Product.FromUmbracoPackageDTO(matching, current as Product);
        }
        return matching;
      }),
    ]) ||
      []) as Product[];
  }

  static filterOutExistingAddons(eligiblePackages: LinkPackageEligibleItem[], addons?: Partial<Product>[]): LinkPackageEligibleItem[] {
    return eligiblePackages.filter((eligiblePackage) => !addons?.some((addon) => addon.serviceName === eligiblePackage.serviceName));
  }

  static AugmentProductBackgroundPatterns(matching: HardwareDTO | undefined, current: Partial<Product>) {
    if (matching) {
      current.mobileBackgroundPattern = matching?.SelfServiceMobileBackgroundPattern
        ? Util.GetMediaPath(matching?.SelfServiceMobileBackgroundPattern)
        : '';
      current.backgroundPattern = matching?.SelfServiceBackgroundPattern ? Util.GetMediaPath(matching?.SelfServiceBackgroundPattern) : '';
    }
    return current;
  }

  static AugmentProductBase(matching: HardwareDTO | undefined, current: Partial<Product>) {
    current.mobileImage = matching?.SelfServiceMobileBackgroundImage ? Util.GetMediaPath(matching?.SelfServiceMobileBackgroundImage) : '';
    current.productCardImage = matching?.SelfServiceProductCardImage ? Util.GetMediaPath(matching?.SelfServiceProductCardImage) : '';
    current.floatingLogoImage = matching?.SelfServiceLogoImage ? Util.GetMediaPath(matching?.SelfServiceLogoImage) : '';
    current.floatingLogoImageDark = matching?.SelfServiceLogoImageDark ? Util.GetMediaPath(matching?.SelfServiceLogoImageDark) : '';
    current.floatingLogoImageLight = matching?.SelfServiceLogoImageLight ? Util.GetMediaPath(matching?.SelfServiceLogoImageLight) : '';
    current.backgroundImage = matching?.SelfServiceBackgroundImage ? Util.GetMediaPath(matching?.SelfServiceBackgroundImage) : '';
    current.color = matching?.SelfServiceProductColor || matching?.PackageColor || '';
    current.heading = matching?.SelfServiceProductHeading || '';
    current.content = matching?.SelfServiceProductContent || '';
    current.description = matching?.SelfServiceProductDescription || '';
    current.code = current.model || '';
    current.features = matching?.Features || '';
    current.iconCode = matching?.IconCode || '';
    current.listedPrice = matching?.Price || null;
    current.productDetail = matching?.ProductDetail || '';
    current.productName = matching?.ProductName || '';
    current.productSummary = matching?.SelfServiceProductSummary || matching?.ProductSummary || '';
    current.productContent = matching?.SelfServiceProductContent || '';
    current.secondaryPackageColor = matching?.SecondaryPackageColor || '';
    current.hardwareIconCode = matching?.SelfServiceIconCode || '';
    current.insurancePrice = matching?.SelfServiceInsurancePrice || (matching?.Price ? `${matching?.Price}` : undefined);
    current.insuranceLabel = matching?.SelfServiceInsuranceLabel;
    current.insurancePriceTerm = matching?.SelfServiceInsurancePriceTerm;
    current.coverAmount = matching?.SelfServiceCoverAmount;
    current.monthlyPrice = matching?.SelfServiceMonthlyPrice;
    current.ageGroup = matching?.SelfServiceAgeGroup;
    current.mobileBackgroundPattern = matching?.SelfServiceMobileBackgroundPattern
      ? Util.GetMediaPath(matching?.SelfServiceMobileBackgroundPattern)
      : '';
    current.backgroundPattern = matching?.SelfServiceBackgroundPattern ? Util.GetMediaPath(matching?.SelfServiceBackgroundPattern) : '';

    return current;
  }

  static MatchCode(contentCodes: string, serviceName?: string, extractCode = false) {
    const possibleCodes = contentCodes.toLowerCase().trim().split(',');
    return possibleCodes.includes(extractCode ? ProductUtils.GetServiceName(serviceName) : `${serviceName}`.toLowerCase().trim());
  }

  static GetServiceName(serviceName?: string) {
    const nameParts = serviceName?.split('-') || [];
    const name = nameParts.length ? nameParts[0].trim() : serviceName;
    return `${name}`.toLowerCase().trim();
  }

  static ShowAddnewAddonCard(product: Product) {
    return product.status === 'Active' || product.status === 'Awaiting Payment';
  }

  static isActionAvailable(action: SubscriptionActions, productActions: ApiActionDTO[]) {
    return productActions.some((_action) => _action.title === action);
  }

  private static getEligibleAgreement(agreements: VASAgreementDTO[], aid: string, scn: string) {
    return (
      agreements.find(({ agreementId, smartcardSerialNumber }) => agreementId === aid || smartcardSerialNumber === scn) ||
      ({} as VASAgreementDTO)
    );
  }

  static MapVasAddonsToPackages(vasAddons: VASAddonDTO, aid: string, scn: string): LinkPackageEligibleItem[] {
    const eligibleAgreement = this.getEligibleAgreement(vasAddons.agreements, aid, scn);

    // if (vasAddons.namola) { eligibleAgreement.namola = vasAddons.namola }

    if (eligibleAgreement) {
      const products = ProductUtils.ExtractQualifyingProducts(eligibleAgreement);
      return products as any
    }

    return [];
  }

  static GetStreamQuantity(vasAddons: VASAddonDTO, aid: string, scn: string): StreamQuantity[] {
    const eligibleAgreement = this.getEligibleAgreement(vasAddons.agreements, aid, scn);

    return eligibleAgreement
      ? Object.values(eligibleAgreement)
        .map((el) => el?.quantities)
        .filter((el) => el)
        .reduce(
          (acm: StreamQuantity[], el: StreamQuantity[]) => [
            ...acm,
            ...el.map((product) => ({
              ...product,
            })),
          ],
          []
        )
      : [];
  }

  static ExtractEligibleProducts(linkableAddOns: LinkPackageEligibleDTO): LinkPackageEligibleItem[] {
    const packageTypes = ['addonPackages', 'accessPackages', 'principalPackages', 'moviesPackages'];
    const eligibleProducts: LinkPackageEligibleItem[] = [];
    for (const packageType of packageTypes) {
      if (linkableAddOns[packageType as keyof LinkPackageEligibleDTO]?.length) {
        eligibleProducts.push(
          ...(linkableAddOns[packageType as keyof LinkPackageEligibleDTO] as LinkPackageEligibleItem[]).map((item) => ({
            ...item,
            originalAmount: item.price?.value,
          }))
        );
      }
    }
    return eligibleProducts;
  }

  static ExtraStream(linkableAddOns: LinkPackageEligibleDTO): LinkPackageEligibleItem[] {
    const packageTypes = ['streamPackages'];
    const eligibleProducts: LinkPackageEligibleItem[] = [];
    for (const packageType of packageTypes) {
      if (linkableAddOns[packageType as keyof LinkPackageEligibleDTO]?.length) {
        eligibleProducts.push(
          ...(linkableAddOns[packageType as keyof LinkPackageEligibleDTO] as LinkPackageEligibleItem[]).map((item) => ({
            ...item,
            originalAmount: item.price?.value,
          }))
        );
      }
    }
    return eligibleProducts;
  }

  static FilterOutDuplicateAddons(vasItems: LinkPackageEligibleItem[], items: LinkPackageEligibleItem[]) {
    const sanitisedItems = items.filter((el) => el);
    return sanitisedItems
      .reduce((acm, addon) => (!acm.find((item) => item.serviceName === addon.serviceName) ? acm.concat(addon) : acm), vasItems)
      .filter((el) => !Array.isArray(el));
  }

  static MapEligibleAddonsAndContent(eligiblePackages: LinkPackageEligibleItem[], content?: PackagesDTO[]) {
    return ((eligiblePackages?.length && [
      ...eligiblePackages
        .map((current) => {
          const matching = content?.find(({ SelfServiceProductCodes }) =>
            ProductUtils.MatchCode(SelfServiceProductCodes, current.serviceName)
          );

          return matching
            ? {
              ...current,
              productName: matching.ProductName,
              mobileImage: matching.SelfServiceMobileBackgroundImage ? Util.GetMediaPath(matching.SelfServiceMobileBackgroundImage) : '',
              productCardImage: matching.SelfServiceProductCardImage ? Util.GetMediaPath(matching.SelfServiceProductCardImage) : '',
              floatingLogoImage: matching.SelfServiceLogoImage ? Util.GetMediaPath(matching.SelfServiceLogoImage) : '',
              floatingLogoImageDark: matching.SelfServiceLogoImageDark ? Util.GetMediaPath(matching.SelfServiceLogoImageDark) : '',
              floatingLogoImageLight: matching.SelfServiceLogoImageLight ? Util.GetMediaPath(matching.SelfServiceLogoImageLight) : '',
              backgroundImage: matching.SelfServiceBackgroundImage ? Util.GetMediaPath(matching.SelfServiceBackgroundImage) : '',
              color: matching.SelfServiceProductColor || matching.PackageColor || '',
              heading: matching.SelfServiceProductHeading || '',
              content: matching.SelfServiceProductContent || '',
              description: matching.SelfServiceProductDescription || '',
              mobileBackgroundPattern: matching.SelfServiceMobileBackgroundPattern
                ? Util.GetMediaPath(matching.SelfServiceMobileBackgroundPattern)
                : '',
              backgroundPattern: matching.SelfServiceBackgroundPattern ? Util.GetMediaPath(matching.SelfServiceBackgroundPattern) : '',
              features: matching.Features || '',
              iconCode: matching.IconCode || '',
              listedPrice: matching.Price || '',
              productDetail: matching.ProductDetail || '',
              productSummary: matching.SelfServiceProductSummary || matching.ProductSummary || '',
              productContent: matching.SelfServiceProductContent || '',
              promoBackgroundImage: matching.SelfServicePromoBackgroundImage || '',
              productCardCTA: matching.SelfServiceProductCardCTA || '',
              productDetailCTA: matching.SelfServiceProductDetailCTA || '',
              code: matching.SelfServiceProductCodes || '',
              logo: Util.GetMediaPath(matching.Logo),
            }
            : null;
        })
        .filter((el) => el),
    ]) ||
      []) as Product[];
  }

  static MapQuotesWithProducts(mainQuote: Quote, primaryProduct: Partial<Product>, eligibleAddons: any[]) {
    return mainQuote.lineItems.map((quote) => {
      const matchingAddon = primaryProduct?.addons?.find((prod) => prod?.productId === quote.entityId);
      const matchingAddonData = eligibleAddons?.find(
        (addon) => matchingAddon?.serviceName && matchingAddon.serviceName === addon.serviceName
      );
      if (matchingAddon) {
        return {
          ...quote,
          ...matchingAddonData,
          lineItems: mainQuote.lineItems,
          id: matchingAddon?.productId,
          logo: matchingAddon?.floatingLogoImage,
          darkLogo: matchingAddon?.floatingLogoImageDark,
          lightLogo: matchingAddon?.floatingLogoImageLight,
          price: quote?.amount,
          dueDate: mainQuote?.paymentDueDate,
          description: matchingAddon?.productSummary?.replace('{date}', ProductUtils.GetOneWeekFromToday()),
          serviceName: matchingAddon?.serviceName,
          productId: matchingAddon?.productId,
          agreementType: matchingAddon?.agreementType,
          iconCode: matchingAddon?.iconCode,
          type: matchingAddon?.type,
        };
      }

      return quote;
    }) as QuoteDetails[];
  }
  static CalculateDiscounts(lineItems: QuoteDetails[]) {
    return lineItems.reduce((acm, item) => {
      const existingItem = acm.findIndex((existing) => existing.entityId === item.entityId);
      if (existingItem >= 0) {
        const sum = acm[existingItem].price + item?.price;
        acm[existingItem].price = sum < 0 ? 0 : sum;
        return [...acm];
      }
      return [...acm, item];
    }, [] as QuoteDetails[]);
  }

  static MoveObjectToStart(array: any[], propertyName: string, propertyValue: string) {
    const index = array.findIndex((obj) => obj[propertyName] === propertyValue);
    if (index !== -1) {
      const objectToMove = array.splice(index, 1)[0];
      array.unshift(objectToMove);
    }
    return array;
  }

  static AddActivationAction = (packages: Partial<Product>[], activationLink: string, content: PackagesDTO[]) => {
    return packages.map((pkg) =>
      ProductUtils.ShowmaxCodeStatus(pkg?.code, content).isZA ? { ...pkg, activationAction: activationLink } : { ...pkg }
    );
  };

  static AddPendingActivation = (packages: Partial<Product>[], isActivationPending: boolean, content: PackagesDTO[]) => {
    return isActivationPending
      ? packages.map((pkg) =>
        ProductUtils.IsShowmax(pkg?.code, content) ? { ...pkg, isActivationPending: isActivationPending } : { ...pkg }
      )
      : packages;
  };

  static AddAddonActivationLabel(products: any, content: PackagesDTO[]) {
    return products.map((product: any) => {
      const hasActivationAction = product.actions?.find((action: { title: string }) => action.title === AvailableProductAction.Activate || action.title === AvailableProductAction.ShowmaxActivate || action.title === AvailableProductAction.ShowmaxActivateEPL);
      return hasActivationAction
        ? {
          ...product,
          isActive: product.isActive,
          hardwareInfo: product.hardwareInfo,
          serviceCode: product.serviceCode,
          addons: this.AddPendingActivation(product.addons, true, content),
        }
        : product;
    }) as Product[];
  }

  private static ExtractQualifyingProducts(agreement: VASAgreementDTO) {
    const VasLinkActions = [
      AvailableProductAction.LinkDisney,
      AvailableProductAction.LinkMovies,
      AvailableProductAction.LinkShowmax,
      AvailableProductAction.LinkStream,
    ];

    return Object.values(agreement)
      .reduce((acc: any[], el: VasProductModel) => {
        if (!el?.actions?.some(action => VasLinkActions.includes(action.title as any))) {
          return acc;
        }

        if (el?.qualifyingProducts?.length) {
          const products = el?.qualifyingProducts.map((qualifyingProduct: QualifyingProductDTO) => {

            if (ProductUtils.IsStream(qualifyingProduct.package)) {
              return {
                ...qualifyingProduct,
                serviceName: qualifyingProduct.package,
                quantities: el.quantities,
                quantity: 0
              };
            }

            return {
              ...qualifyingProduct,
              serviceName: qualifyingProduct.package,
            };
          });

          return [...acc, ...products];
        }

        return acc;
      }, []);
  }

  static GetShowmaxContent(content: PackagesDTO[] = []) {
    return content?.filter((contentItem) => contentItem.Code.toLowerCase().includes('showmax'));
  }

  static GetStreamContent(content: PackagesDTO[] = []) {
    return content?.find((contentItem) => contentItem.Code.toLowerCase().includes('stream'));
  }

  private static ShowmaxCodes(showMaxContent: PackagesDTO[]) {
    var outputArray = [] as string[];
    showMaxContent.forEach((contentItem) => {
      outputArray = outputArray.concat(contentItem.SelfServiceProductCodes.split(','));
    });
    return outputArray;
  }

  private static StreamCodes(streamContent: PackagesDTO) {
    return streamContent.SelfServiceProductCodes.split(',');
  }

  static IsShowmax(code: string = '', content: PackagesDTO[] = []) {
    const showmaxCodeStatus = this.ShowmaxCodeStatus(code, content);
    return showmaxCodeStatus.isZA || showmaxCodeStatus.isMAH || showmaxCodeStatus.isMAHOTT;
  }

  static IsShowmaxPL(code: string = '', content: PackagesDTO[] = []) {
    const showmaxCodeStatus = this.ShowmaxPLCodeStatus(code, content);
    return showmaxCodeStatus.isZA || showmaxCodeStatus.isMAH;
  }

  static sanitiseCode(code: string = '') {
    return code.toLowerCase().replace(/[ -]/g, '');
  }

  static ShowmaxCodeStatus(code?: string, content?: PackagesDTO[]) {
    const showmaxLocal = { isZA: false, isMAH: false, isMAHOTT: false } as ShowmaxCodeLocal;
    const showmaxContent = this.GetShowmaxContent(content);
    this.GetStreamContent(content);

    if (!code || !showmaxContent) {
      return showmaxLocal;
    }

    const allCodes = this.ShowmaxCodes(showmaxContent).map((c) => this.sanitiseCode(c));
    const zaCodes = allCodes.filter((c) => c.includes('addon'));
    const mahOTTCodes = allCodes.filter((c) => c.includes(this.sanitiseCode(VasCodesType.SHOWMAXOTT)));
    const mahCodes = [
      ...mahOTTCodes,
      ...allCodes.filter(
        (c) => c.includes(this.sanitiseCode(VasCodesType.SHOWMAXE36)) || c.includes(this.sanitiseCode(VasCodesType.SHOWMAXIS20))
      ),
    ];
    const sanitisedCode = this.sanitiseCode(code);
    showmaxLocal.isZA = zaCodes.includes(sanitisedCode);
    showmaxLocal.isMAH = mahCodes.includes(sanitisedCode);
    showmaxLocal.isMAHOTT = mahOTTCodes.includes(sanitisedCode);
    return showmaxLocal;
  }

  static ShowmaxPLCodeStatus(code?: string, content?: PackagesDTO[]) {
    const showmaxLocal = { isZA: false, isMAH: false } as ShowmaxCodePL;
    const showmaxContent = this.GetShowmaxContent(content);
    this.GetStreamContent(content);

    if (!code || !showmaxContent) {
      return showmaxLocal;
    }

    const allCodes = this.ShowmaxCodes(showmaxContent).map((c) => this.sanitiseCode(c));
    const zaCodes = allCodes.filter((c) => c.includes(this.sanitiseCode(VasCodesType.SHOWMAXAddonPL)));
    const mahCodes = allCodes.filter((c) => c.includes(this.sanitiseCode("SHOWMAXPL")));;
    const sanitisedCode = this.sanitiseCode(code);
    showmaxLocal.isZA = zaCodes.includes(sanitisedCode);
    showmaxLocal.isMAH = mahCodes.includes(sanitisedCode);
    return showmaxLocal;
  }

  static IsStream(code?: string, content?: PackagesDTO[]) {
    const streamContent = this.GetStreamContent(content);

    if (!code) {
      return false;
    }
    const sanitisedCode = this.sanitiseCode(code);

    if (!streamContent) {
      return sanitisedCode === this.sanitiseCode(VasCodesType.STREAMMOBILE);
    }
    const allCodes = this.StreamCodes(streamContent).map((code) => this.sanitiseCode(code));

    return allCodes.includes(sanitisedCode);
  }

  static getMobileStream(services: Partial<Product>[], content?: PackagesDTO[]): Partial<Product> | undefined {
    return services.find((service) => this.IsStream(service.serviceName, content));
  }

  private static GetOneWeekFromToday() {
    const oneWeekFromToday = moment().add(7, 'days');
    const formattedDate = oneWeekFromToday.format('D MMMM YYYY');
    return formattedDate;
  }
}